import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import AdminPage from '../../components/admin/AdminPage';
import Loader from '../../components/common/Loader';
import NoData from '../../components/common/NoData';
import { getGenericSurvey, assignGenericSurvey, getTraineeGroups, getTraineesInGroup } from '../../services/adminService';
import { getTraineeDetails } from '../../services/traineeService';

function SurveyAssign() {
  const navigate = useNavigate();
  const { id } = useParams();
  const surveyId = Number(id);

  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [survey, setSurvey] = useState(null);

  const [mode, setMode] = useState('individual'); // individual | group
  const [trainees, setTrainees] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [selectedGroups, setSelectedGroups] = useState(new Set());

  const normalizeTrainees = (resp) => {
    const candidate =
      resp?.data?.users ??
      resp?.data?.trainees ??
      resp?.data?.data ??
      resp?.data?.rows ??
      resp?.data;

    if (Array.isArray(candidate)) return candidate;
    if (Array.isArray(candidate?.users)) return candidate.users;
    if (Array.isArray(candidate?.trainees)) return candidate.trainees;
    if (Array.isArray(candidate?.data)) return candidate.data;
    if (Array.isArray(candidate?.rows)) return candidate.rows;
    return [];
  };

  const filtered = useMemo(() => {
    const list = Array.isArray(trainees) ? trainees : [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((t) => {
      const name = String(t.name || '').toLowerCase();
      const email = String(t.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [trainees, search]);

  const filteredGroups = useMemo(() => {
    const list = Array.isArray(groups) ? groups : [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((g) => {
      const name = String(g.name || '').toLowerCase();
      const desc = String(g.description || '').toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [groups, search]);

  const fetchData = async () => {
    if (!surveyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [sResp, tResp, gResp] = await Promise.all([
        getGenericSurvey(surveyId),
        getTraineeDetails({ page: 1, limit: 2000, status: 'all', search: '' }),
        getTraineeGroups(),
      ]);

      if (!sResp?.success) {
        toast.error(sResp?.message || 'Survey not found');
        setSurvey(null);
      } else {
        setSurvey(sResp.data?.survey);
      }

      if (tResp?.success) {
        setTrainees(normalizeTrainees(tResp));
      } else {
        setTrainees([]);
      }

      if (gResp?.success) {
        const gs = Array.isArray(gResp?.data?.groups)
          ? gResp.data.groups
          : Array.isArray(gResp?.data)
            ? gResp.data
            : [];
        setGroups(gs);
      } else {
        setGroups([]);
      }
    } catch (e) {
      toast.error('Failed to load data');
      setSurvey(null);
      setTrainees([]);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [surveyId]);

  const toggle = (id2) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id2)) next.delete(id2);
      else next.add(id2);
      return next;
    });
  };

  const toggleAll = () => {
    if (mode !== 'individual') return;
    if (filtered.length === 0) return;
    const allSelected = filtered.every((t) => selected.has(t.id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const t of filtered) next.delete(t.id);
      } else {
        for (const t of filtered) next.add(t.id);
      }
      return next;
    });
  };

  const toggleGroup = (gid) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  };

  const toggleAllGroups = () => {
    if (mode !== 'group') return;
    if (filteredGroups.length === 0) return;
    const allSelected = filteredGroups.every((g) => selectedGroups.has(g.id));
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const g of filteredGroups) next.delete(g.id);
      } else {
        for (const g of filteredGroups) next.add(g.id);
      }
      return next;
    });
  };

  const handleAssign = async () => {
    let ids = [];

    if (mode === 'group') {
      const gids = Array.from(selectedGroups);
      if (gids.length === 0) {
        toast.warning('Select groups to assign');
        return;
      }

      try {
        const respList = await Promise.all(
          gids.map((gid) => getTraineesInGroup(gid, { page: 1, limit: 2000, search: '' }))
        );

        const users = respList.flatMap((r) => {
          if (!r?.success) return [];
          const t = r?.data?.trainees || r?.data || [];
          return Array.isArray(t) ? t : [];
        });

        ids = [...new Set(users.map((u) => u.id).filter(Boolean))];
      } catch (e) {
        toast.error('Failed to fetch trainees from groups');
        return;
      }

      if (ids.length === 0) {
        toast.warning('No trainees found in selected groups');
        return;
      }
    } else {
      ids = Array.from(selected);
      if (ids.length === 0) {
        toast.warning('Select trainees to assign');
        return;
      }
    }

    setAssigning(true);
    try {
      const resp = await assignGenericSurvey(surveyId, { user_ids: ids });
      if (!resp?.success) {
        toast.error(resp?.message || 'Failed to assign');
        return;
      }
      toast.success('Survey assigned');
      navigate(`/admin/surveys/${surveyId}`);
    } catch (e) {
      toast.error('Failed to assign');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <AdminPage
        back={
          <button
            type="button"
            onClick={() => navigate(`/admin/surveys/${surveyId}`)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
          >
            <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
            Back
          </button>
        }
        title="Assign Survey"
      >
        <Loader message="Loading trainees..." />
      </AdminPage>
    );
  }

  if (!survey) {
    return (
      <AdminPage
        back={
          <button
            type="button"
            onClick={() => navigate('/admin/surveys')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
          >
            <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
            Back
          </button>
        }
        title="Assign Survey"
      >
        <NoData message="Survey not found" />
      </AdminPage>
    );
  }

  return (
    <AdminPage
      back={
        <button
          type="button"
          onClick={() => navigate(`/admin/surveys/${surveyId}`)}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
        >
          <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
          Back
        </button>
      }
      title="Assign Survey"
      description={`Assign "${survey.title}" to trainees.`}
      actions={
        <button
          type="button"
          onClick={handleAssign}
          disabled={assigning}
          className="inline-flex items-center gap-2 rounded-xl bg-primary text-white px-4 py-2 text-sm font-semibold hover:opacity-95 disabled:opacity-60"
        >
          {assigning ? (
            <>
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Assigning
            </>
          ) : (
            <>
              <Icon icon="fluent:send-24-regular" width="18" height="18" />
              Assign ({mode === 'group' ? selectedGroups.size : selected.size})
            </>
          )}
        </button>
      }
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="inline-flex rounded-2xl border border-gray-200 bg-white p-1">
          <button
            type="button"
            onClick={() => {
              setMode('individual');
              setSearch('');
            }}
            className={`px-3 py-2 rounded-2xl text-sm font-semibold ${
              mode === 'individual' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Individuals
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('group');
              setSearch('');
            }}
            className={`px-3 py-2 rounded-2xl text-sm font-semibold ${
              mode === 'group' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Groups
          </button>
        </div>
        <div className="text-xs text-gray-500">
          {mode === 'group' ? 'Assign by trainee groups' : 'Assign by selecting trainees'}
        </div>
      </div>

      <div className="mb-4">
        <div className="relative max-w-md">
          <Icon icon="fluent:search-24-regular" width="18" height="18" className="text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-2xl border border-gray-200 bg-white pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder={mode === 'group' ? 'Search groups by name...' : 'Search trainees by name or email...'}
          />
        </div>
      </div>

      {mode === 'group' ? (
        filteredGroups.length === 0 ? (
          <NoData message={groups.length === 0 ? 'No groups found' : 'No matching groups'} />
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-3 border-b border-gray-100 flex items-center justify-between">
              <button
                type="button"
                onClick={toggleAllGroups}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                <Icon icon="fluent:select-all-24-regular" width="18" height="18" />
                Toggle page
              </button>
              <div className="text-xs text-gray-500">{filteredGroups.length} shown</div>
            </div>

            <div className="divide-y divide-gray-100">
              {filteredGroups.map((g) => {
                const checked = selectedGroups.has(g.id);
                return (
                  <label key={g.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={checked} onChange={() => toggleGroup(g.id)} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-900 truncate">{g.name || '—'}</div>
                      <div className="text-xs text-gray-500 truncate">{g.description || ''}</div>
                    </div>
                    <div className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-gray-200 bg-gray-50 text-gray-700">
                      {g.user_count || 0} members
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )
      ) : filtered.length === 0 ? (
        <NoData message={trainees.length === 0 ? 'No trainees found' : 'No matching trainees'} />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <button
              type="button"
              onClick={toggleAll}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
            >
              <Icon icon="fluent:select-all-24-regular" width="18" height="18" />
              Toggle page
            </button>
            <div className="text-xs text-gray-500">{filtered.length} shown</div>
          </div>

          <div className="divide-y divide-gray-100">
            {filtered.map((t) => {
              const checked = selected.has(t.id);
              return (
                <label key={t.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={checked} onChange={() => toggle(t.id)} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-900 truncate">{t.name || '—'}</div>
                    <div className="text-xs text-gray-500 truncate">{t.email || ''}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </AdminPage>
  );
}

export default SurveyAssign;
